import React, { createContext, useContext, useEffect, useState } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { startOfMonth } from 'date-fns';

import storage from '@dashboard/services/storage/storage';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import { Select } from '@dashboard/components/Form';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CardContent from '@dashboard/components/CardContent';
import Card from '@dashboard/components/Card';
import Link from '@dashboard/components/Link';
import CardHeaderActions from '@dashboard/components/CardHeaderActions';
import Button from '@dashboard/components/Button';

import { dateRanges, dateRangeUnits } from './date-ranges';

import RecentPayments from './components/RecentPayments';
import RecentSubscriptions from './components/RecentSubscriptions';
import RecentCustomers from './components/RecentCustomers';
import ActiveSubscribers from './components/ActiveSubscribers';
import NewSales from './components/NewSales';
import Views from './components/Views';
import MonthlyRecurringRevenue from './components/MonthlyRecurringRevenue';
import Revenue from './components/Revenue.js';
import RecentSubmissions from './components/RecentSubmissions';
import OnboardingSteps from './components/OnboardingSteps';

function checkHasStatisticsData(seller) {
  if (seller?.disabled) return false;

  const allowedPlanHandles = ['premium', 'launch', 'grow', 'scale'];
  const hasAllowedPlan = allowedPlanHandles.includes(seller?.plan?.handle);

  if (!hasAllowedPlan) {
    return false;
  }

  const allowedSubscriptionStatuses = ['active', 'trialing'];
  const hasAllowedSubscriptionStatus = allowedSubscriptionStatuses.includes(
    seller?.subscriptionStatus,
  );

  if (!hasAllowedSubscriptionStatus) {
    return false;
  }

  return true;
}

const StatisticsContext = createContext();

export const useStatisticsContext = () => useContext(StatisticsContext);

function getTimezoneOffset() {
  const offsetMinutesTotal = new Date().getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offsetMinutesTotal) / 60);
  const offsetMinutes = Math.abs(offsetMinutesTotal) % 60;

  const timezoneOffset =
    (offsetMinutesTotal > 0 ? '-' : '+') +
    String(offsetHours).padStart(2, '0') +
    ':' +
    String(offsetMinutes).padStart(2, '0');

  return timezoneOffset;
}

export default function StatisticsDashboard() {
  const { seller } = useAuth();
  const [range, setRange] = useState(storage.local.getItem('statistics-range') || 'monthToDate');
  const [unit, setUnit] = useState(storage.local.getItem('statistics-unit') || dateRangeUnits.DAY);

  useEffect(() => {
    storage.local.setItem('statistics-range', range);
  }, [range]);

  useEffect(() => {
    storage.local.setItem('statistics-unit', unit);
  }, [unit]);

  let dateStart;
  if (range === 'allTime' && seller?.createdAt) {
    dateStart = startOfMonth(new Date(seller.createdAt));
  } else {
    dateStart = dateRanges[range].start;
  }

  const dateEnd = dateRanges[range].end;

  const hasStatisticsData = checkHasStatisticsData(seller);

  function handleSetRange(range) {
    setRange(range);

    const dateRange = dateRanges[range];

    if (!dateRange.units.includes(unit)) {
      setUnit(dateRange.defaultUnit);
    }
  }

  const timezoneOffset = getTimezoneOffset();

  return (
    <StatisticsContext.Provider
      value={{
        hasStatisticsData,
        range,
        unit,
        setRange: handleSetRange,
        setUnit,
        dateStart,
        dateEnd,
        timezoneOffset,
      }}
    >
      <Dashboard />
    </StatisticsContext.Provider>
  );
}

function Dashboard() {
  const { range, setRange, unit, setUnit } = useStatisticsContext();

  return (
    <Card variant="root">
      <CardHeader variant="root">
        <CardHeaderTitle variant="root">Home</CardHeaderTitle>

        <CardHeaderActions>
          <Select onChange={e => setRange(e.target.value)} className="!w-42">
            {Object.keys(dateRanges).map(dateRange => (
              <option key={dateRange} value={dateRange} selected={dateRange === range}>
                {dateRanges[dateRange].label}
              </option>
            ))}
          </Select>

          <Select
            onChange={e => {
              setUnit(e.target.value);
            }}
            className="!w-28"
          >
            {dateRanges[range].units.includes(dateRangeUnits.HOUR) && (
              <option value={dateRangeUnits.HOUR} selected={unit === 'hour'}>
                Hourly
              </option>
            )}

            {dateRanges[range].units.includes(dateRangeUnits.DAY) && (
              <option value={dateRangeUnits.DAY} selected={unit === 'day'}>
                Daily
              </option>
            )}

            {dateRanges[range].units.includes(dateRangeUnits.WEEK) && (
              <option value={dateRangeUnits.WEEK} selected={unit === 'week'}>
                Weekly
              </option>
            )}

            {dateRanges[range].units.includes(dateRangeUnits.MONTH) && (
              <option value={dateRangeUnits.MONTH} selected={unit === 'month'}>
                Monthly
              </option>
            )}

            {dateRanges[range].units.includes(dateRangeUnits.YEAR) && (
              <option value={dateRangeUnits.YEAR} selected={unit === 'year'}>
                Yearly
              </option>
            )}
          </Select>

          <Button href="/pages/new" appearance="primary" className="whitespace-nowrap" wide>
            Create page
            <ArrowRightIcon className="w-4 h-4 ml-2 align-baseline" />
          </Button>
        </CardHeaderActions>
      </CardHeader>

      <OnboardingSteps />

      <div className="grid xl:grid-cols-6 2xl:grid-cols-9">
        <StatisticsCard type="chart">
          <Revenue />
        </StatisticsCard>

        <StatisticsCard type="chart">
          <MonthlyRecurringRevenue />
        </StatisticsCard>

        <StatisticsCard type="chart">
          <ActiveSubscribers />
        </StatisticsCard>

        <StatisticsCard type="chart">
          <NewSales />
        </StatisticsCard>

        <StatisticsCard type="chart">
          <Views />
        </StatisticsCard>

        <StatisticsCard type="list">
          <Card variant="none">
            <CardHeader variant="card">
              <CardHeaderTitle variant="card">
                <div className="text-base font-normal">New payments</div>
              </CardHeaderTitle>

              <div>
                <Link
                  href="/payments/one-time"
                  className="flex items-center gap-x-1 whitespace-nowrap text-sm"
                >
                  View all <ArrowRightIcon className="w-4 h-4" />
                </Link>
              </div>
            </CardHeader>

            <CardContent variant="card">
              <RecentPayments />
            </CardContent>
          </Card>
        </StatisticsCard>

        <StatisticsCard type="list">
          <Card variant="none">
            <CardHeader variant="card">
              <CardHeaderTitle variant="card">
                <div className="text-base font-normal">New subscriptions</div>
              </CardHeaderTitle>
              <div>
                <Link
                  href="/payments/subscriptions"
                  className="flex items-center gap-x-1 whitespace-nowrap text-sm"
                >
                  View all <ArrowRightIcon className="w-4 h-4" />
                </Link>
              </div>
            </CardHeader>
            <CardContent variant="card">
              <RecentSubscriptions />
            </CardContent>
          </Card>
        </StatisticsCard>

        <StatisticsCard type="list">
          <Card variant="none">
            <CardHeader variant="card">
              <CardHeaderTitle variant="card">
                <div className="text-base font-normal">New submissions</div>
              </CardHeaderTitle>

              <div>
                <Link
                  href="/submissions"
                  className="flex items-center gap-x-1 whitespace-nowrap text-sm"
                >
                  View all <ArrowRightIcon className="w-4 h-4" />
                </Link>
              </div>
            </CardHeader>

            <CardContent variant="card">
              <RecentSubmissions />
            </CardContent>
          </Card>
        </StatisticsCard>

        <StatisticsCard type="list">
          <Card variant="none">
            <CardHeader variant="card">
              <CardHeaderTitle variant="card">
                <div className="text-base font-normal">New customers</div>
              </CardHeaderTitle>
              <div>
                <Link
                  href="/customers"
                  className="flex items-center gap-x-1 whitespace-nowrap text-sm"
                >
                  View all <ArrowRightIcon className="w-4 h-4" />
                </Link>
              </div>
            </CardHeader>
            <CardContent variant="card">
              <RecentCustomers />
            </CardContent>
          </Card>
        </StatisticsCard>
      </div>
    </Card>
  );
}

function StatisticsCard({ children, type = 'chart' }) {
  const { hasStatisticsData } = useStatisticsContext();
  const { isAuthenticating } = useAuth();

  if (!hasStatisticsData && type === 'chart' && !isAuthenticating) {
    return (
      <div className={clsx('col-span-3 border-b border-r border-gray-100 p-4 py-8 relative')}>
        <div className="absolute inset-0 bg-white opacity-75 z-10 flex items-center justify-center">
          <div className="text-center text-gray-700">
            Start a subscription to see your statistics
          </div>
        </div>

        {children}
      </div>
    );
  }

  return (
    <div className={clsx('col-span-3 border-b border-r border-gray-100 p-4 py-8 min-h-[360px]')}>
      {children}
    </div>
  );
}
